import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';
import { Wallet, Search, Link2, Trophy, User, Calendar, RefreshCw } from 'lucide-react';
import { overlayClose } from '../lib/overlayClose';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => `R$ ${Number(v || 0).toFixed(2)}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('pt-BR') : '—');
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('pt-BR') : '—');

const monthStart = () => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1).toISOString().substring(0, 10); };
const todayISO = () => new Date().toISOString().substring(0, 10);

export const CashCustomersPage = () => {
  const [tab, setTab] = useState('ranking');
  return (
    <Layout>
      <div className="p-8" data-testid="cash-customers-page">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-md bg-emerald-600 flex items-center justify-center">
            <Wallet size={24} className="text-white" />
          </div>
          <div>
            <h1 className="text-4xl font-black text-slate-900 tracking-tight">Clientes à Vista</h1>
            <p className="text-sm text-slate-600">Total gasto à vista por cliente — não altera o crediário (a prazo)</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-6">
          <TabsList className="grid w-full md:w-[560px] grid-cols-3 mb-6">
            <TabsTrigger value="ranking" data-testid="tab-cash-ranking"><Trophy size={15} className="mr-1" /> Ranking</TabsTrigger>
            <TabsTrigger value="buscar" data-testid="tab-cash-search"><Search size={15} className="mr-1" /> Buscar Cliente</TabsTrigger>
            <TabsTrigger value="vincular" data-testid="tab-cash-link"><Link2 size={15} className="mr-1" /> Vincular Vendas</TabsTrigger>
          </TabsList>

          <TabsContent value="ranking"><RankingTab /></TabsContent>
          <TabsContent value="buscar"><SearchCustomerTab /></TabsContent>
          <TabsContent value="vincular"><LinkSalesTab /></TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

/* ---------- filtros de período ---------- */
const PeriodFilter = ({ range, setRange, onApply }) => (
  <div className="flex items-end gap-2 flex-wrap">
    <div>
      <Label className="text-[10px] uppercase font-bold text-slate-500">De</Label>
      <Input type="date" value={range.start} className="h-9 w-40" data-testid="cash-start-date"
        onChange={(e) => setRange({ ...range, start: e.target.value })} />
    </div>
    <div>
      <Label className="text-[10px] uppercase font-bold text-slate-500">Até</Label>
      <Input type="date" value={range.end} className="h-9 w-40" data-testid="cash-end-date"
        onChange={(e) => setRange({ ...range, end: e.target.value })} />
    </div>
    <Button variant="outline" className="h-9" onClick={onApply} data-testid="cash-apply-period"><Calendar size={15} className="mr-1" /> Aplicar</Button>
  </div>
);

/* ---------- RANKING ---------- */
const RankingTab = () => {
  const [range, setRange] = useState({ start: monthStart(), end: todayISO() });
  const [search, setSearch] = useState('');
  const [data, setData] = useState({ items: [], totals: {} });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (r, q) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/customers/cash-report`, {
        params: { start_date: r.start, end_date: r.end, search: q || undefined }, withCredentials: true,
      });
      setData(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(range, ''); }, [load]); // eslint-disable-line

  return (
    <div data-testid="cash-ranking">
      <div className="bg-white rounded-md border border-slate-200 p-4 mb-4 flex flex-wrap items-end justify-between gap-3">
        <PeriodFilter range={range} setRange={setRange} onApply={() => load(range, search)} />
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-[10px] uppercase font-bold text-slate-500">Buscar por nome</Label>
            <Input value={search} placeholder="Nome do cliente" className="h-9 w-56" data-testid="cash-ranking-search"
              onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') load(range, search); }} />
          </div>
          <Button className="h-9 bg-emerald-600 hover:bg-emerald-700" onClick={() => load(range, search)} data-testid="cash-ranking-search-btn">Buscar</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Card label="Total à vista (período)" value={fmt(data.totals?.total)} accent="text-emerald-600" />
        <Card label="Compras" value={data.totals?.count || 0} accent="text-slate-900" />
        <Card label="Clientes" value={data.totals?.customers || 0} accent="text-slate-900" />
      </div>

      <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <Th>#</Th><Th>Cliente</Th><Th>Telefone</Th><Th className="text-center">Compras</Th><Th>Última compra</Th><Th className="text-right">Total à vista</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {loading && <tr><td colSpan={6} className="text-center py-8 text-slate-400">Carregando...</td></tr>}
            {!loading && data.items.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">Nenhum cliente com compras à vista no período. Use "Vincular Vendas" para registrar.</td></tr>}
            {!loading && data.items.map((c, i) => (
              <tr key={c.customer_id} className="hover:bg-slate-50" data-testid={`ranking-row-${c.customer_id}`}>
                <td className="px-4 py-3 text-sm font-bold text-slate-400">{i + 1}</td>
                <td className="px-4 py-3 text-sm font-medium text-slate-900">{c.name}</td>
                <td className="px-4 py-3 text-sm text-slate-600">{c.phone || '—'}</td>
                <td className="px-4 py-3 text-sm text-center">{c.count}</td>
                <td className="px-4 py-3 text-sm text-slate-600">{fmtDate(c.last_purchase)}</td>
                <td className="px-4 py-3 text-right font-bold text-emerald-600">{fmt(c.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ---------- BUSCAR CLIENTE ---------- */
const SearchCustomerTab = () => {
  const [range, setRange] = useState({ start: monthStart(), end: todayISO() });
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const searchCustomers = async (text) => {
    setQ(text);
    if (!text || text.length < 2) { setResults([]); return; }
    try {
      const r = await axios.get(`${API}/customers`, { params: { search: text }, withCredentials: true });
      setResults(r.data.slice(0, 8));
    } catch (e) { console.error(e); }
  };

  const loadDetail = useCallback(async (cust, r) => {
    if (!cust) return;
    try {
      const res = await axios.get(`${API}/customers/${cust.id}/cash-sales`, {
        params: { start_date: r.start, end_date: r.end }, withCredentials: true,
      });
      setDetail(res.data);
    } catch (e) { console.error(e); }
  }, []);

  const pick = (c) => { setSelected(c); setResults([]); setQ(c.name); loadDetail(c, range); };

  return (
    <div data-testid="cash-search">
      <div className="bg-white rounded-md border border-slate-200 p-4 mb-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="relative">
            <Label className="text-[10px] uppercase font-bold text-slate-500">Cliente</Label>
            <Input value={q} placeholder="Digite o nome ou telefone" className="h-9 w-72" data-testid="cash-customer-search"
              onChange={(e) => searchCustomers(e.target.value)} />
            {results.length > 0 && (
              <div className="absolute z-20 mt-1 w-72 bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-y-auto" data-testid="cash-customer-results">
                {results.map((c) => (
                  <button key={c.id} onClick={() => pick(c)} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm border-b last:border-0" data-testid={`cash-customer-option-${c.id}`}>
                    <span className="font-medium text-slate-900">{c.name}</span>
                    {c.phone && <span className="text-slate-400 ml-2">{c.phone}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <PeriodFilter range={range} setRange={setRange} onApply={() => loadDetail(selected, range)} />
        </div>
      </div>

      {!selected && <p className="text-sm text-slate-400 py-8 text-center">Busque e selecione um cliente para ver o histórico de compras à vista.</p>}

      {selected && detail && (
        <div data-testid="cash-customer-detail">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <Card label="Cliente" value={detail.customer?.name} accent="text-slate-900" small />
            <Card label="Total à vista (período)" value={fmt(detail.totals?.total)} accent="text-emerald-600" />
            <Card label="Compras" value={detail.totals?.count || 0} accent="text-slate-900" />
          </div>
          <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200"><tr>
                <Th>Venda</Th><Th>Data</Th><Th className="text-center">Itens</Th><Th>Pagamento</Th><Th className="text-right">Total</Th>
              </tr></thead>
              <tbody className="divide-y divide-slate-200">
                {detail.sales.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-400">Sem compras à vista no período</td></tr>}
                {detail.sales.map((s, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm font-medium">#{s.sale_number}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{fmtDateTime(s.created_at)}</td>
                    <td className="px-4 py-3 text-sm text-center">{s.items_count}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 capitalize">{(s.payment_method || '').replace('_', ' ')}</td>
                    <td className="px-4 py-3 text-right font-bold text-emerald-600">{fmt(s.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------- VINCULAR VENDAS ---------- */
const LinkSalesTab = () => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picker, setPicker] = useState(null); // sale being linked
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/sales/cash-unlinked`, { params: { limit: 100 }, withCredentials: true });
      setSales(r.data.items || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const searchCustomers = async (text) => {
    setQ(text);
    if (!text || text.length < 2) { setResults([]); return; }
    try {
      const r = await axios.get(`${API}/customers`, { params: { search: text }, withCredentials: true });
      setResults(r.data.slice(0, 8));
    } catch (e) { console.error(e); }
  };

  const linkTo = async (cust) => {
    try {
      await axios.put(`${API}/sales/${picker.id}/customer`, { customer_id: cust.id }, { withCredentials: true });
      setPicker(null); setQ(''); setResults([]);
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Erro ao vincular'); }
  };

  return (
    <div data-testid="cash-link">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-600">Vendas à vista ainda sem cliente. Clique em <strong>Vincular</strong> para registrar quem comprou.</p>
        <Button variant="outline" className="h-9" onClick={load} data-testid="cash-link-refresh"><RefreshCw size={15} className={loading ? 'animate-spin' : ''} /></Button>
      </div>

      <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            <Th>Venda</Th><Th>Data</Th><Th>Item</Th><Th>Pagamento</Th><Th className="text-right">Total</Th><Th>Ação</Th>
          </tr></thead>
          <tbody className="divide-y divide-slate-200">
            {loading && <tr><td colSpan={6} className="text-center py-8 text-slate-400">Carregando...</td></tr>}
            {!loading && sales.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">Nenhuma venda à vista sem cliente 🎉</td></tr>}
            {!loading && sales.map((s) => (
              <tr key={s.id} className="hover:bg-slate-50" data-testid={`unlinked-row-${s.id}`}>
                <td className="px-4 py-3 text-sm font-medium">#{s.sale_number}</td>
                <td className="px-4 py-3 text-sm text-slate-600">{fmtDateTime(s.created_at)}</td>
                <td className="px-4 py-3 text-sm text-slate-600 truncate max-w-[220px]">{s.first_item || '—'}{s.items_count > 1 ? ` +${s.items_count - 1}` : ''}</td>
                <td className="px-4 py-3 text-sm text-slate-600 capitalize">{(s.payment_method || '').replace('_', ' ')}</td>
                <td className="px-4 py-3 text-right font-bold text-slate-900">{fmt(s.total)}</td>
                <td className="px-4 py-3">
                  <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" onClick={() => { setPicker(s); setQ(''); setResults([]); }} data-testid={`link-btn-${s.id}`}>
                    <User size={14} className="mr-1" /> Vincular
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {picker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" {...overlayClose(() => setPicker(null))}>
          <div className="bg-white rounded-lg p-6 w-96" onClick={(e) => e.stopPropagation()} data-testid="link-customer-modal">
            <h3 className="text-lg font-bold text-slate-900 mb-1">Vincular venda #{picker.sale_number}</h3>
            <p className="text-sm text-slate-500 mb-4">{fmt(picker.total)} · {fmtDateTime(picker.created_at)}</p>
            <Label className="text-xs uppercase font-bold text-slate-500">Cliente</Label>
            <Input autoFocus value={q} placeholder="Buscar cliente por nome ou telefone" data-testid="link-customer-search"
              onChange={(e) => searchCustomers(e.target.value)} />
            <div className="mt-2 border border-slate-200 rounded-md max-h-56 overflow-y-auto">
              {results.length === 0 && <p className="text-sm text-slate-400 p-3">Digite ao menos 2 letras…</p>}
              {results.map((c) => (
                <button key={c.id} onClick={() => linkTo(c)} className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-sm border-b last:border-0" data-testid={`link-option-${c.id}`}>
                  <span className="font-medium text-slate-900">{c.name}</span>
                  {c.phone && <span className="text-slate-400 ml-2">{c.phone}</span>}
                </button>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <Button variant="outline" onClick={() => setPicker(null)}>Cancelar</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Card = ({ label, value, accent, small }) => (
  <div className="bg-white rounded-md border border-slate-200 p-4">
    <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">{label}</p>
    <p className={`${small ? 'text-xl' : 'text-2xl'} font-black tabular-nums mt-1 ${accent} truncate`}>{value}</p>
  </div>
);

const Th = ({ children, className = '' }) => (
  <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500 ${className}`}>{children}</th>
);

export default CashCustomersPage;
